const HEALTH_STATUS_PREFIX = "COMPLIANCE_INTEGRATION_HEALTH_STATUS_";
const ERROR_CODE_PREFIX = "COMPLIANCE_INTEGRATION_HEALTH_ERROR_CODE_";

const HEALTH_STATUS_BY_NUMBER = ["unspecified", "validating", "healthy", "degraded", "action_required", "not_ready"];
const ERROR_CODE_BY_NUMBER = [
    "none",
    "credential_invalid",
    "permission_denied",
    "wrong_key_type",
    "configuration_error",
    "rate_limited",
    "upstream_error",
    "transport_error",
    "response_invalid",
    "pagination_incomplete",
    "internal_error",
];

const HEALTH_STATUSES = new Set(HEALTH_STATUS_BY_NUMBER);
const ERROR_CODES = new Set(ERROR_CODE_BY_NUMBER);

function normalizeEnum(value, prefix, byNumber, allowed, fallback) {
    if (Number.isInteger(value)) return byNumber[value] ?? fallback;
    if (typeof value !== "string") return fallback;
    const normalized = value.startsWith(prefix)
        ? value.slice(prefix.length).toLowerCase()
        : value.toLowerCase();
    return allowed.has(normalized) ? normalized : fallback;
}

function timestamp(raw, camel, snake) {
    const value = raw?.[camel] ?? raw?.[snake] ?? null;
    return typeof value === "string" && value ? value : null;
}

export function normalizeComplianceIntegration(raw) {
    const configured = Boolean(raw?.configured);
    const legacyErrorPresent = Boolean(raw?.lastSyncError ?? raw?.last_sync_error);
    const structuredHealthPresent = raw != null && (
        Object.prototype.hasOwnProperty.call(raw, "healthStatus")
        || Object.prototype.hasOwnProperty.call(raw, "health_status")
    );
    let healthStatus = normalizeEnum(
        raw?.healthStatus ?? raw?.health_status,
        HEALTH_STATUS_PREFIX,
        HEALTH_STATUS_BY_NUMBER,
        HEALTH_STATUSES,
        configured ? "validating" : "unspecified",
    );
    let healthErrorCode = normalizeEnum(
        raw?.healthErrorCode ?? raw?.health_error_code,
        ERROR_CODE_PREFIX,
        ERROR_CODE_BY_NUMBER,
        ERROR_CODES,
        "none",
    );
    if (configured && !structuredHealthPresent && legacyErrorPresent) {
        healthStatus = "degraded";
        healthErrorCode = "internal_error";
    }
    const failureCount = Number(raw?.consecutiveFailures ?? raw?.consecutive_failures ?? 0);
    return {
        configured,
        status: typeof raw?.status === "string" ? raw.status : "",
        healthStatus,
        healthErrorCode,
        lastCheckedAt: timestamp(raw, "lastCheckedAt", "last_checked_at"),
        lastSuccessAt: timestamp(raw, "lastSuccessAt", "last_success_at"),
        lastErrorAt: timestamp(raw, "lastErrorAt", "last_error_at"),
        consecutiveFailures: Number.isFinite(failureCount) && failureCount > 0
            ? Math.floor(failureCount)
            : 0,
    };
}

const ERROR_COPY = Object.freeze({
    credential_invalid: "The Compliance Access Key is no longer valid. Replace the key to resume Anthropic capture.",
    permission_denied: "The Compliance Access Key does not have the required permissions. Replace it with a key that has the required compliance read scopes.",
    wrong_key_type: "The saved key is not an Anthropic Compliance Access Key. Replace it with a Compliance Access Key from Anthropic.",
    configuration_error: "Anthropic Compliance access is not available for this organization. Check the organization's Compliance API configuration.",
    rate_limited: "Anthropic is temporarily rate limiting capture. Origin will keep retrying automatically.",
    upstream_error: "Anthropic is temporarily unavailable. Origin will keep retrying automatically.",
    transport_error: "Origin cannot currently reach Anthropic. Origin will keep retrying automatically.",
    response_invalid: "Anthropic returned an unexpected response. Origin will keep retrying automatically.",
    pagination_incomplete: "Anthropic capture could not finish reading the current activity page. Origin will retry it automatically.",
    internal_error: "Anthropic capture encountered an internal error. Origin will keep retrying automatically.",
    none: "Anthropic capture is not healthy. Origin will keep checking the connection.",
});

const DEGRADED_ERROR_COPY = Object.freeze({
    credential_invalid: "Anthropic capture encountered an authentication error while importing activity. Origin will keep retrying automatically.",
    permission_denied: "Anthropic capture is blocked by a permissions error while importing activity. Origin will keep retrying automatically. Check compliance access if this continues.",
    wrong_key_type: "Anthropic capture encountered an access error while importing activity. Origin will keep retrying automatically.",
    configuration_error: "Anthropic capture is blocked by a request or configuration error while importing activity. Origin will keep retrying automatically. Check the organization's Compliance API configuration if this continues.",
    rate_limited: "Anthropic capture could not finish importing activity because of rate limiting. Origin will keep retrying automatically.",
    upstream_error: "Anthropic capture could not finish importing activity because Anthropic returned an error. Origin will keep retrying automatically.",
    transport_error: "Anthropic capture could not finish importing activity because Origin could not reach Anthropic. Origin will keep retrying automatically.",
    response_invalid: "Anthropic capture could not finish importing activity because Anthropic returned an unexpected response. Origin will keep retrying automatically.",
    pagination_incomplete: ERROR_COPY.pagination_incomplete,
    internal_error: "Anthropic capture could not finish importing activity because of an internal error. Origin will keep retrying automatically.",
    none: "Anthropic capture could not finish importing activity. Origin will keep retrying automatically.",
});

export function complianceHealthPresentation(integration) {
    if (!integration?.configured) {
        return {
            label: "Not configured",
            impact: "Add a Compliance Access Key to start importing Anthropic activity.",
            tone: "neutral",
        };
    }
    switch (integration.healthStatus) {
        case "healthy":
            return {
                label: "Capturing",
                impact: "Origin can connect to Anthropic and import compliance activity.",
                tone: "healthy",
            };
        case "degraded":
            return {
                label: "Degraded",
                impact: DEGRADED_ERROR_COPY[integration.healthErrorCode] ?? DEGRADED_ERROR_COPY.none,
                tone: "warning",
            };
        case "action_required":
            return {
                label: "Needs attention",
                impact: ERROR_COPY[integration.healthErrorCode] ?? ERROR_COPY.none,
                tone: "critical",
            };
        case "not_ready":
            return {
                label: "Setup required",
                impact: "Anthropic capture is not running because no directory scope is available to authorize which people can be imported. Configure an authorizing directory scope to start capture.",
                tone: "warning",
            };
        case "validating":
        case "unspecified":
        default:
            return {
                label: "Checking",
                impact: "The key is saved. Origin is checking its Anthropic access before capture starts.",
                tone: "neutral",
            };
    }
}

export function formatComplianceHealthTime(value) {
    if (!value) return "Never";
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return "Never";
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
}
